import { ApolloQueryResult } from '@apollo/client';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IGroupObject,
  IObject,
  IObjectInput,
  IObjectLimit,
  IObjectManagementStreamPreview,
  ISocialProfileAutoComplete,
  TObjectStatusFilter,
  TObjectFilter,
  TStreamTypeFilter
} from 'src/models/objectManagement';
// import { IAutoClippingPayload } from 'src/models/onlineMedia';
import { IEntityOutput, ITempClippingInput } from 'src/services/useOnboarding';

export interface ITempInputKeywordOrHashtag extends IObjectInput {
  id?: string;
}

interface IObjectManagement {
  socialProfileAutoComplete: ApolloQueryResult<
    Array<ISocialProfileAutoComplete>
  >;
  tempInputObjects: Array<IObjectInput>; // for onboarding
  tempInputObjectsCompetitor: Array<IObjectInput>;
  objectList: ApolloQueryResult<Array<IObject>>;
  ecommerceObjectList: Array<IObject>;
  objectListWithStatus: Array<IObject>;
  hashtagList: ApolloQueryResult<Array<IObject>>;
  groupObject: ApolloQueryResult<Array<IGroupObject>>;
  groupSelectedById: string;
  objectListByObjectType: Array<IObject>; // current active objects
  tempInputKeywordOrHashtag: Array<ITempInputKeywordOrHashtag>; // for onboarding
  streamPreview: ApolloQueryResult<IObjectManagementStreamPreview | undefined>;
  objectLimit: ApolloQueryResult<IObjectLimit>;
  // for filter object list in setting page
  objectType: TObjectFilter;
  selectedStreamType: TStreamTypeFilter;
  selectedObjectStatus: TObjectStatusFilter;
  // for filter object list in analytics pages
  selectedObjectType: TObjectFilter;
  // for metrics onboarding
  tempInputClipping: Array<ITempClippingInput>;
  tempInputEntity: Array<IEntityOutput>;
  selectedGroups: Array<IGroupObject>;
  selectedEcommerceGroups: Array<IGroupObject>;
  selectedTempInputKeywordHashtag: ITempInputKeywordOrHashtag;
  selectedTempInputSocmed: IObjectInput;
  selectedTempClipping: ITempClippingInput;
}

const initialState: IObjectManagement = {
  socialProfileAutoComplete: {
    data: [],
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  tempInputObjects: [],
  tempInputObjectsCompetitor: [],
  objectList: {
    data: [],
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  ecommerceObjectList: [],
  objectListWithStatus: [],
  objectListByObjectType: [],
  hashtagList: {
    data: [],
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  tempInputKeywordOrHashtag: [],
  streamPreview: {
    data: undefined,
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  groupObject: {
    data: [],
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  groupSelectedById: '_default',
  objectLimit: {
    data: {
      accountQuota: 0,
      accountUsed: 0,
      keywordQuota: 0,
      keywordUsed: 0
    },
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  objectType: 'all',
  selectedObjectType: 'all',
  tempInputClipping: [],
  tempInputEntity: [],
  selectedGroups: [],
  selectedEcommerceGroups: [],
  selectedStreamType: 'all',
  selectedObjectStatus: 'all',
  selectedTempInputKeywordHashtag: null,
  selectedTempInputSocmed: null,
  selectedTempClipping: null
};

export const objectManagement = createSlice({
  name: 'objectManagement',
  initialState,
  reducers: {
    reducerUpdateSocialProfileAutoCompleteLoading: (
      state: IObjectManagement,
      action: PayloadAction<boolean>
    ) => {
      state.socialProfileAutoComplete = {
        ...state.socialProfileAutoComplete,
        loading: action.payload
      };
    },
    reducerUpdateSocialProfileAutoComplete: (
      state: IObjectManagement,
      action: PayloadAction<
        ApolloQueryResult<Array<ISocialProfileAutoComplete>>
      >
    ) => {
      state.socialProfileAutoComplete = action.payload;
    },
    reducerClearSocialProfileAutoComplete: (state: IObjectManagement) => {
      state.socialProfileAutoComplete.data = [];
    },
    reducerUpdateTempObjectInput: (
      state: IObjectManagement,
      action: PayloadAction<IObjectInput>
    ) => {
      state.tempInputObjects = [...state.tempInputObjects, action.payload];
    },
    reducerUpdateAllTempObjectInput: (
      state: IObjectManagement,
      action: PayloadAction<IObjectInput[]>
    ) => {
      state.tempInputObjects = action.payload;
    },
    reducerRemoveTempObjectInput: (
      state: IObjectManagement,
      action: PayloadAction<string>
    ) => {
      state.tempInputObjects = state.tempInputObjects.filter(
        (object) => object.content !== action.payload
      );
    },
    reducerClearTempObjectInput: (
      state: IObjectManagement,
      action: PayloadAction<string>
    ) => {
      state.tempInputObjects = [];
    },
    reducerUpdateTempKeywordOrHashtag: (
      state: IObjectManagement,
      action: PayloadAction<ITempInputKeywordOrHashtag[]>
    ) => {
      state.tempInputKeywordOrHashtag = [
        ...state.tempInputKeywordOrHashtag,
        ...action.payload
      ];
    },
    reducerUpdateAllTempKeywordHashtag: (
      state: IObjectManagement,
      action: PayloadAction<ITempInputKeywordOrHashtag[]>
    ) => {
      state.tempInputKeywordOrHashtag = action.payload;
    },
    reducerRemoveTempKeywordOrHashtag: (
      state: IObjectManagement,
      action: PayloadAction<string>
    ) => {
      state.tempInputKeywordOrHashtag = state.tempInputKeywordOrHashtag.filter(
        (object) => object.id !== action.payload
      );
    },
    reducerUpdateObjectListByObjectType: (
      state: IObjectManagement,
      action: PayloadAction<Array<IObject>>
    ) => {
      state.objectListByObjectType = action.payload;
    },
    reducerUpdateTempObjectInputCompetitor: (
      state: IObjectManagement,
      action: PayloadAction<IObjectInput>
    ) => {
      state.tempInputObjectsCompetitor = [
        ...state.tempInputObjectsCompetitor,
        action.payload
      ];
    },
    reducerRemoveTempObjectInputCompetitor: (
      state: IObjectManagement,
      action: PayloadAction<string>
    ) => {
      state.tempInputObjectsCompetitor =
        state.tempInputObjectsCompetitor.filter(
          (object) => object.content !== action.payload
        );
    },
    reducerUpdateObjectList: (
      state: IObjectManagement,
      action: PayloadAction<ApolloQueryResult<Array<IObject>>>
    ) => {
      state.objectList = action.payload;
    },
    reducerUpdateEcommerceObjectList: (
      state: IObjectManagement,
      action: PayloadAction<Array<IObject>>
    ) => {
      state.ecommerceObjectList = action.payload;
    },
    reducerUpdateHashtagList: (
      state: IObjectManagement,
      action: PayloadAction<ApolloQueryResult<Array<IObject>>>
    ) => {
      state.hashtagList = action.payload;
    },
    reducerUpdateGroupObject: (
      state: IObjectManagement,
      action: PayloadAction<ApolloQueryResult<Array<IGroupObject>>>
    ) => {
      state.groupObject = action.payload;
    },
    reducerUpdateObjectManagementStreamPreview: (
      state: IObjectManagement,
      action: PayloadAction<ApolloQueryResult<IObjectManagementStreamPreview>>
    ) => {
      state.streamPreview = action.payload;
    },
    reducerUpdateObjectManagementStreamPreviewLoading: (
      state: IObjectManagement,
      action: PayloadAction<boolean>
    ) => {
      state.streamPreview.loading = action.payload;
    },
    reducerClearObjectManagementStreamPreview: (
      state: IObjectManagement,
      action: PayloadAction<undefined>
    ) => {
      state.streamPreview.data = action.payload;
    },
    reducerUpdateObjectManagementGroupSelectedById: (
      state: IObjectManagement,
      action: PayloadAction<string | undefined>
    ) => {
      state.groupSelectedById = action.payload;
    },
    reducerUpdateObjectManagementLimit: (
      state: IObjectManagement,
      action: PayloadAction<ApolloQueryResult<IObjectLimit>>
    ) => {
      state.objectLimit = action.payload;
    },
    reducerUpdateObjectType: (
      state: IObjectManagement,
      action: PayloadAction<TObjectFilter>
    ) => {
      state.objectType = action.payload;
    },
    reducerUpdateSelectedObjectType: (
      state: IObjectManagement,
      action: PayloadAction<TObjectFilter>
    ) => {
      state.selectedObjectType = action.payload;
    },
    reducerUpdateTempInputClipping: (
      state: IObjectManagement,
      action: PayloadAction<ITempClippingInput>
    ) => {
      state.tempInputClipping = [...state.tempInputClipping, action.payload];
    },
    reducerUpdateAllTempInputClipping: (
      state: IObjectManagement,
      action: PayloadAction<ITempClippingInput[]>
    ) => {
      state.tempInputClipping = action.payload;
    },
    reducerUpdateSelectedTempInputClipping: (
      state: IObjectManagement,
      action: PayloadAction<ITempClippingInput>
    ) => {
      state.selectedTempClipping = action.payload;
    },
    reducerRemoveTempInputClipping: (
      state: IObjectManagement,
      action: PayloadAction<string>
    ) => {
      state.tempInputClipping = state.tempInputClipping.filter(
        (clipping) => clipping.id !== action.payload
      );
    },
    reducerUpdateTempEntityInput: (
      state: IObjectManagement,
      action: PayloadAction<IEntityOutput>
    ) => {
      state.tempInputEntity = [...state.tempInputEntity, action.payload];
    },
    reducerRemoveTempEntityInput: (
      state: IObjectManagement,
      action: PayloadAction<string>
    ) => {
      state.tempInputEntity = state.tempInputEntity.filter(
        (object) => object.entityId !== action.payload
      );
    },
    reducerClearTempEntityInput: (
      state: IObjectManagement
      // action: PayloadAction<string>
    ) => {
      state.tempInputEntity = [];
    },
    reducerUpdateSelectedGroups: (
      state: IObjectManagement,
      action: PayloadAction<IGroupObject[]>
    ) => {
      state.selectedGroups = action.payload;
    },
    reducerUpdateSelectedEcommerceGroups: (
      state: IObjectManagement,
      action: PayloadAction<IGroupObject[]>
    ) => {
      state.selectedEcommerceGroups = action.payload;
    },
    reducerUpdateSelectedStreamType: (
      state: IObjectManagement,
      action: PayloadAction<TStreamTypeFilter>
    ) => {
      state.selectedStreamType = action.payload;
    },
    reducerUpdateSelectedObjectStatus: (
      state: IObjectManagement,
      action: PayloadAction<TObjectStatusFilter>
    ) => {
      state.selectedObjectStatus = action.payload;
    },
    reducerUpdateSelectedTempKeywordHashtag: (
      state: IObjectManagement,
      action: PayloadAction<ITempInputKeywordOrHashtag>
    ) => {
      state.selectedTempInputKeywordHashtag = action.payload;
    },
    reducerUpdateSelectedTempSocmed: (
      state: IObjectManagement,
      action: PayloadAction<IObjectInput>
    ) => {
      state.selectedTempInputSocmed = action.payload;
    },
    reducerUpdateObjectListWithStatus: (
      state: IObjectManagement,
      action: PayloadAction<Array<IObject>>
    ) => {
      state.objectListWithStatus = action.payload;
    }
  }
});

export const {
  reducerUpdateSocialProfileAutoComplete,
  reducerUpdateSocialProfileAutoCompleteLoading,
  reducerClearSocialProfileAutoComplete,
  reducerUpdateTempObjectInput,
  reducerUpdateAllTempObjectInput,
  reducerRemoveTempObjectInput,
  reducerClearTempObjectInput,
  reducerUpdateTempObjectInputCompetitor,
  reducerRemoveTempObjectInputCompetitor,
  reducerUpdateObjectList,
  reducerUpdateGroupObject,
  reducerUpdateTempKeywordOrHashtag,
  reducerUpdateAllTempKeywordHashtag,
  reducerRemoveTempKeywordOrHashtag,
  reducerUpdateObjectListByObjectType,
  reducerUpdateObjectManagementStreamPreview,
  reducerUpdateObjectManagementStreamPreviewLoading,
  reducerClearObjectManagementStreamPreview,
  reducerUpdateObjectManagementGroupSelectedById,
  reducerUpdateObjectManagementLimit,
  reducerUpdateHashtagList,
  reducerUpdateObjectType,
  reducerUpdateSelectedObjectType,
  reducerUpdateTempInputClipping,
  reducerUpdateAllTempInputClipping,
  reducerUpdateSelectedTempInputClipping,
  reducerRemoveTempInputClipping,
  reducerUpdateTempEntityInput,
  reducerRemoveTempEntityInput,
  reducerClearTempEntityInput,
  reducerUpdateSelectedGroups,
  reducerUpdateSelectedStreamType,
  reducerUpdateSelectedObjectStatus,
  reducerUpdateSelectedTempKeywordHashtag,
  reducerUpdateObjectListWithStatus,
  reducerUpdateSelectedEcommerceGroups,
  reducerUpdateEcommerceObjectList,
  reducerUpdateSelectedTempSocmed
} = objectManagement.actions;

export default objectManagement.reducer;
